// src/host/HostSettings.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../lib/useAuth";
import { auth, db, storage } from "../firebase";
import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";

/** ------------------------------ Utils ------------------------------ **/
function clsx(...xs) {
  return xs.filter(Boolean).join(" ");
}
function formatPhone(s) {
  if (!s) return "";
  const digits = String(s).replace(/\D+/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return s;
}
function Badge({ tone = "neutral", children }) {
  const tones = {
    neutral: "border-neutral-300 bg-white text-neutral-700",
    good: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    bad: "border-red-200 bg-red-50 text-red-700",
    info: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs",
        tones[tone] || tones.neutral
      )}
    >
      {children}
    </span>
  );
}

/** -------- Simple client-side image compression (avatar) -------- **/
const AVATAR_MAX_DIM = 512;
const AVATAR_QUALITY = 0.84;

function canvasToBlob(canvas, type = "image/jpeg", quality = AVATAR_QUALITY) {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((b) => resolve(b), type, quality);
    } else {
      const dataUrl = canvas.toDataURL(type, quality);
      const byteString = atob(dataUrl.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      resolve(new Blob([ab], { type }));
    }
  });
}

async function compressImage(file, maxDim = AVATAR_MAX_DIM, quality = AVATAR_QUALITY) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = (e) => rej(e);
      i.src = url;
    });

    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await canvasToBlob(canvas, "image/jpeg", quality);
    return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** ------------------------------- Page ------------------------------- **/
export default function HostSettings() {
  const { user, loading } = useAuth();

  const [docData, setDocData] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Form fields
  const [displayName, setDisplayName] = useState("");
  const [company, setCompany] = useState("");
  const [bio, setBio] = useState("");
  const [phone, setPhone] = useState("");
  const [photoURL, setPhotoURL] = useState(""); // current avatar

  // UI state
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const fileInputRef = useRef(null);
  const emailVerified = !!user?.emailVerified;

  // Load users/{uid} live
  useEffect(() => {
    if (loading || !user) return;

    const ref_ = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref_,
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setDocData(d);

        // Prime fields (only once on first load)
        if (!initialLoaded) {
          setDisplayName(d.displayName || user.displayName || "");
          setCompany(d.company || "");
          setBio(d.bio || "");
          setPhone(d.phone || "");
          setPhotoURL(d.photoURL || user.photoURL || "");
          setInitialLoaded(true);
        }
      },
      (e) => {
        console.error(e);
        setErr("Couldn't load settings.");
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, loading, user, initialLoaded]);

  const dirtyProfile = useMemo(() => {
    if (!initialLoaded) return false;

    const baseDisplayName = docData?.displayName || user?.displayName || "";
    const baseCompany = docData?.company || "";
    const baseBio = docData?.bio || "";
    const basePhone = docData?.phone || "";
    const basePhoto = docData?.photoURL || user?.photoURL || "";

    return (
      (displayName || "") !== (baseDisplayName || "") ||
      (company || "") !== (baseCompany || "") ||
      (bio || "") !== (baseBio || "") ||
      (phone || "") !== (basePhone || "") ||
      (photoURL || "") !== (basePhoto || "")
    );
  }, [initialLoaded, displayName, company, bio, phone, photoURL, docData, user]);

  /** --------------------------- Actions --------------------------- **/

  async function saveProfile() {
    if (!user) return;
    setErr("");
    setMsg("");
    setSavingProfile(true);

    try {
      const ref_ = doc(db, "users", user.uid);

      // Update Auth profile for displayName / photoURL
      const needsAuthUpdate =
        (displayName || "") !== (user.displayName || "") ||
        ((photoURL || "") !== (user.photoURL || ""));

      if (needsAuthUpdate) {
        await updateProfile(auth.currentUser, {
          displayName: displayName || "",
          photoURL: photoURL || null,
        });
      }

      // Ensure user doc exists, then update fields
      await setDoc(
        ref_,
        {
          displayName: displayName || "",
          company: company || "",
          bio: bio || "",
          phone: phone || "",
          photoURL: photoURL || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setMsg("Profile saved.");
    } catch (e) {
      console.error(e);
      setErr("Couldn't save profile. Please try again.");
    } finally {
      setSavingProfile(false);
    }
  }

  async function onPickAvatar(e) {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setErr("");
    setMsg("");
    setUploading(true);
    setUploadPct(0);

    try {
      const img = await compressImage(file, AVATAR_MAX_DIM, AVATAR_QUALITY);

      const key = `avatars/${user.uid}-${Date.now()}.jpg`;
      const storageRef = ref(storage, key);

      const meta = {
        contentType: img.type || "image/jpeg",
        cacheControl: "public, max-age=31536000, immutable",
        customMetadata: { uploadedBy: user.uid },
      };

      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(storageRef, img, meta);
        task.on(
          "state_changed",
          (snap) => {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            setUploadPct(pct);
          },
          (err) => reject(err),
          () => resolve(null)
        );
      });

      const url = await getDownloadURL(storageRef);
      setPhotoURL(url);
      setMsg("Photo uploaded. Remember to Save profile.");
    } catch (e) {
      console.error(e);
      setErr("Upload failed. Try a different image.");
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function sendVerifyEmail() {
    if (!auth.currentUser) return;
    setErr("");
    setMsg("");

    try {
      await sendEmailVerification(auth.currentUser);
      setMsg("Verification email sent. Check your inbox.");
    } catch (e) {
      console.error(e);
      setErr("Couldn't send verification email right now.");
    }
  }

  /** ---------------------------- Render ---------------------------- **/

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="mt-2 text-neutral-600">
          Profile and verification.
        </p>
        <div className="mt-6 rounded-2xl border border-neutral-200 p-6">
          <div className="h-32 animate-pulse rounded-xl bg-neutral-200" />
        </div>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-2xl font-semibold">Settings</h1>
      <p className="mt-2 text-neutral-600">
        Profile and verification.
      </p>

      {msg && (
        <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-800">
          {msg}
        </div>
      )}
      {err && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      <div className="mt-6 grid gap-6">
        {/* Profile */}
        <section className="rounded-2xl border border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Profile</h2>
            <Badge tone="neutral">Public</Badge>
          </div>
          <p className="mt-1 text-sm text-neutral-600">
            Your host name, company, bio and photo may appear on your listings.
          </p>

          <div className="mt-4 grid gap-4 sm:grid-cols-[120px,1fr] sm:items-start">
            {/* Avatar column */}
            <div className="flex flex-col items-center gap-3">
              <div className="h-24 w-24 overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-100">
                {photoURL ? (
                  <img
                    src={photoURL}
                    alt="Avatar"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-neutral-500">
                    No photo
                  </div>
                )}
              </div>

              <div className="text-xs text-neutral-500">
                JPG/PNG • up to ~5MB
              </div>

              <button
                onClick={() => fileInputRef.current?.click()}
                className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:bg-neutral-50"
                disabled={uploading}
              >
                {uploading ? "Uploading…" : "Change photo"}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickAvatar}
              />

              {uploading && (
                <div className="w-24">
                  <div className="h-2 rounded-full bg-neutral-200">
                    <div
                      className="h-2 rounded-full bg-[#D4AF37] transition-all"
                      style={{ width: `${uploadPct}%` }}
                    />
                  </div>
                  <div className="mt-1 text-center text-[10px] text-neutral-500">
                    {uploadPct}%
                  </div>
                </div>
              )}
            </div>

            {/* Form column */}
            <div className="grid gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm">Public name</label>
                  <input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                    placeholder="e.g., Jamie C."
                  />
                </div>

                <div>
                  <label className="text-sm">Company (optional)</label>
                  <input
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                    placeholder="e.g., Riverside Studios"
                  />
                  {company && (
                    <div className="mt-1 text-xs text-neutral-500">
                      Helps admins and guests understand who’s hosting.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm">About</label>
                <textarea
                  rows={4}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  placeholder="Short bio about you and your space."
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm">
                    Phone (for booking coordination)
                  </label>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                    placeholder="(555) 555-1234"
                  />
                  {phone && (
                    <div className="mt-1 text-xs text-neutral-500">
                      Stored privately • {formatPhone(phone)}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm">Email</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      value={user.email || ""}
                      readOnly
                      className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600"
                    />
                    <Badge tone={emailVerified ? "good" : "warn"}>
                      {emailVerified ? "Verified" : "Unverified"}
                    </Badge>
                  </div>

                  {!emailVerified && (
                    <button
                      onClick={sendVerifyEmail}
                      className="mt-2 rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:bg-neutral-50"
                    >
                      Send verification email
                    </button>
                  )}
                </div>
              </div>

              <div className="pt-2">
                <button
                  onClick={saveProfile}
                  disabled={!dirtyProfile || savingProfile}
                  className="rounded-full bg-black px-5 py-2 text-sm font-semibold text-white hover:bg-[#B8860B] disabled:opacity-60"
                >
                  {savingProfile ? "Saving…" : "Save profile"}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Verification */}
        <section className="rounded-2xl border border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Verification</h2>
            <Badge tone={emailVerified ? "good" : "warn"}>
              {emailVerified ? "Email verified" : "Email not verified"}
            </Badge>
          </div>

          <p className="mt-1 text-sm text-neutral-600">
            Email verification helps us keep the community safe. Identity
            verification will be handled by Stripe during payout onboarding.
          </p>

          <div className="mt-4 grid gap-3 text-sm">
            <div className="flex items-center justify-between">
              <span>Email</span>
              <span className="font-medium">{user.email || "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Identity (KYC)</span>
              <span className="text-neutral-500">
                Handled in Stripe Express (on Payouts page)
              </span>
            </div>
          </div>

          <div className="mt-4">
            {!emailVerified ? (
              <button
                onClick={sendVerifyEmail}
                className="rounded-full border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50"
              >
                Send verification email
              </button>
            ) : (
              <div className="text-sm text-neutral-600">
                Thanks! If your status doesn’t update immediately, it will after
                you sign out/in.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
